<!-- @format -->

<template>
	<div
		:class="['dnb-match-card', { 'dnb-match-card--selected': selected }]"
		@click="$emit('select')"
	>
		<div class="dnb-match-card__avatar">
			<CoreIcon
				:name="selected ? 'account-arrow-left' : 'account-outline'"
				:size="28"
				:fillColor="selected ? 'var(--color-primary-700)' : 'var(--color-neutral-900)'"
			/>
		</div>
		<div class="dnb-match-card__main">
			<p :class="['dnb-match-card__name', { 'dnb-match-card__name--matching': nameMatches }]">{{ person.first_name }} {{ person.last_name }}</p>
			<div class="dnb-match-card__meta">
				<div class="dnb-match-card__meta-item">
					<span class="dnb-match-card__meta-label">
						{{ $t('§eq_operator.auth_person_date_of_birth') }}
					</span>
					<span :class="['dnb-match-card__meta-value', { 'dnb-match-card__meta-value--matching': matchingFields.date_of_birth }]">
						{{ formattedBirthDate }}
					</span>
				</div>
				<div class="dnb-match-card__meta-item">
					<span class="dnb-match-card__meta-label">
						{{ $t('§eq_operator.auth_person_country') }}
					</span>
					<span :class="['dnb-match-card__meta-value', { 'dnb-match-card__meta-value--matching': matchingFields.country_id }]">
						{{ countryLabel || '—' }}
					</span>
				</div>
				<div
					v-if="person.created_at"
					class="dnb-match-card__meta-item"
				>
					<span class="dnb-match-card__meta-label">
						{{ $t('§eq_operator.dnb_match_card_added') }}
					</span>
					<span class="dnb-match-card__meta-value">
						{{ formattedAddedDate }}
					</span>
				</div>
			</div>
		</div>
		<Badge
			v-tooltip="{ value: $t('§eq_operator.dnb_match_card_score', { score }) }"
			class="dnb-match-card__badge"
			:value="$t(tierLabelKey)"
			:severity="tierSeverity"
		/>
	</div>
</template>

<script>
import { parseISO } from 'date-fns'
import Badge from 'primevue/badge'
import CoreIcon from '@/components/core/CoreIcon.vue'

const TIER_LABEL_KEYS = {
	confirmed: '§eq_operator.dnb_confidence_confirmed',
	likely: '§eq_operator.dnb_confidence_likely',
	possible: '§eq_operator.dnb_confidence_possible',
}

const TIER_SEVERITIES = {
	confirmed: 'success',
	likely: 'warn',
	possible: 'secondary',
}

export default {
	name: 'DnbMatchCard',

	components: {
		Badge,
		CoreIcon,
	},

	props: {
		person: {
			type: Object,
			required: true,
		},
		score: {
			type: Number,
			required: true,
		},
		tier: {
			type: String,
			required: true,
			validator: (v) => ['confirmed', 'likely', 'possible'].includes(v),
		},
		selected: {
			type: Boolean,
			default: false,
		},
		countryLabel: {
			type: String,
			default: '',
		},
		matchingFields: {
			type: Object,
			default: () => ({}),
		},
	},

	emits: ['select'],

	computed: {
		nameMatches() {
			return !!(this.matchingFields.first_name && this.matchingFields.last_name)
		},

		tierLabelKey() {
			return TIER_LABEL_KEYS[this.tier]
		},

		tierSeverity() {
			return TIER_SEVERITIES[this.tier]
		},

		formattedBirthDate() {
			if (!this.person.date_of_birth) return '—'
			try {
				return this.displayAsDate(parseISO(this.person.date_of_birth))
			} catch {
				return '—'
			}
		},

		formattedAddedDate() {
			if (!this.person.created_at) return '—'
			try {
				return this.displayAsDate(parseISO(this.person.created_at))
			} catch {
				return '—'
			}
		},
	},
}
</script>

<style>
.dnb-match-card {
	cursor: pointer;
	display: flex;
	align-items: flex-start;
	gap: 1rem;
	padding: 1rem 1.25rem;
	border: 1px solid var(--color-neutral-700);
	border-radius: 8px;
	background-color: var(--color-white, #fff);
	transition:
		border-color 0.15s ease,
		background-color 0.15s ease;
}

.dnb-match-card:hover {
	background-color: var(--color-neutral-300);
	border-color: var(--color-neutral-800);
}

.dnb-match-card--selected {
	border-color: var(--color-primary);
	background-color: var(--color-primary-300);
}

.dnb-match-card--selected:hover {
	background-color: var(--color-primary-300);
	border-color: var(--color-primary);
}

.dnb-match-card__avatar {
	flex: 0 0 auto;
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background-color: var(--color-neutral-500);
	display: flex;
	align-items: center;
	justify-content: center;
}

.dnb-match-card--selected .dnb-match-card__avatar {
	background-color: var(--color-primary-200);
}

.dnb-match-card__main {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.6rem;
}

.dnb-match-card__name {
	font-weight: 600;
	font-size: 1.6rem;
	color: var(--text-dark);
	margin: 0;
}

.dnb-match-card__meta {
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
}

.dnb-match-card__meta-item {
	display: flex;
	flex-direction: column;
}

.dnb-match-card__meta-label {
	color: var(--color-neutral-900);
	font-size: 1.2rem;
}

.dnb-match-card__meta-value {
	color: var(--text-dark);
	font-size: 1.4rem;
	font-weight: 500;
}

.dnb-match-card__name--matching {
	color: var(--color-primary);
}

.dnb-match-card__meta-value--matching {
	color: var(--color-primary);
}

.dnb-match-card__badge {
	flex: 0 0 auto;
	align-self: flex-start;
	font-weight: 600;
}

.dnb-match-card__badge.p-badge-success {
	color: var(--color-primary-900);
}

.dnb-match-card__badge.p-badge-warn {
	color: var(--color-yellow-900);
}

.dnb-match-card__badge.p-badge-secondary {
	color: var(--color-neutral-900);
}
</style>
